// client/src/pages/seo/DigitaleSpeisekarteVergleichDe.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "check",
    label: "Welche digitale Speisekarte braucht Ihr Restaurant?",
  },
  {
    id: "definition",
    label: "Was bedeutet ein Vergleich digitaler Speisekarten?",
  },
  {
    id: "criteria",
    label: "Die wichtigsten Vergleichskriterien",
  },
  {
    id: "provider-types",
    label: "Welche Arten von Anbietern gibt es?",
  },
  {
    id: "pricing",
    label: "Kosten vergleichen: kostenlos, günstig oder langfristig sinnvoll?",
  },
  {
    id: "features",
    label: "Welche Funktionen sind wirklich wichtig?",
  },
  {
    id: "seo",
    label: "Warum Sichtbarkeit und eine Restaurantseite beim Vergleich wichtig sind",
  },
  {
    id: "mistakes",
    label: "Häufige Fehler beim Vergleich digitaler Speisekarten",
  },
  {
    id: "bestplace",
    label: "Wo Best Place in diesen Vergleich passt",
  },
  {
    id: "faq",
    label: "FAQ zum Vergleich digitaler Speisekarten",
  },
];

const checklist = [
  "Sie vergleichen mehrere Anbieter für digitale Speisekarten.",
  "Sie möchten nicht nur nach dem niedrigsten Preis entscheiden.",
  "Sie benötigen QR-Code, Online-Speisekarte oder mobilen Menüzugang.",
  "Sie möchten Preise, Fotos und Verfügbarkeit selbst bearbeiten.",
  "Sie könnten später Reservierungs- oder Bestellfunktionen benötigen.",
  "Sie möchten wissen, ob ein kostenloser Plan für den Start ausreicht.",
  "Sie brauchen eine Lösung, die für Gäste auf dem Smartphone gut funktioniert.",
  "Sie möchten weniger von PDFs, Druckkosten oder manuellen Änderungen abhängig sein.",
  "Sie möchten Restaurantseite, digitale Speisekarte und QR-Code langfristig verbinden.",
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function getResult(score) {
  if (score <= 3) {
    return {
      title: "Ein sehr einfacher Anbieter kann ausreichen",
      text:
        "Wenn Sie nur eine kleine Speisekarte online anzeigen möchten, kann eine einfache QR- oder Menü-Lösung genügen. Achten Sie trotzdem darauf, dass die Speisekarte auf Smartphones wirklich gut funktioniert.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "Vergleichen Sie nicht nur nach dem Preis",
      text:
        "Für Ihr Restaurant sind mehrere Kriterien wichtig: Bearbeitung, QR-Code, mobile Ansicht, Fotos, Verfügbarkeit und mögliche Erweiterungen in der Zukunft.",
      tone: "medium",
    };
  }

  return {
    title: "Sie brauchen wahrscheinlich eine erweiterbare Lösung",
    text:
      "Mehrere Punkte sprechen dafür, dass Ihr Restaurant einen Anbieter wählen sollte, der mehr kann als nur eine Speisekarte anzeigen: Verwaltung, QR-Code, Website, Reservierungen und zukünftige Erweiterungen spielen eine wichtige Rolle.",
    tone: "high",
  };
}

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Idee für Illustration">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idee für Illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function DigitalMenuComparisonDe() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  useEffect(() => {
    document.documentElement.setAttribute("lang", "de");
    document.title =
      "Digitale Speisekarten im Vergleich: Worauf Restaurants achten sollten | Best Place";
  }, []);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="de"
        langLinks={{
          en: "/en/digital-menu-comparison",
          de: "/de/digitale-speisekarte-vergleich",
          no: "/no/digital-meny-sammenligning",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Digitale Speisekarten im Vergleich: Worauf Restaurants wirklich
              achten sollten
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Die beste digitale Speisekarte ist nicht automatisch die
                günstigste Lösung oder die mit der längsten Funktionsliste.
                Entscheidend ist, ob sie im echten Restaurantalltag hilft:
                Speisekarte aktualisieren, QR-Zugang nutzen, Preise ändern,
                Gäste informieren und später weiterwachsen.
              </p>

              <p className="seoHero__text">
                Dieser Leitfaden zeigt, wie Restaurants Anbieter für digitale
                Speisekarten vergleichen können, welche Kriterien wirklich
                wichtig sind und warum eine digitale Speisekarte mehr sein
                sollte als nur eine PDF-Datei hinter einem QR-Code.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="de" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Inhalt des Artikels</h2>

        <nav className="qrArticle__nav" aria-label="Artikelnavigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="check" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Welche digitale Speisekarte braucht Ihr Restaurant?</h2>

            <p>
              Wählen Sie die Aussagen aus, die zu Ihrem Restaurant passen. Das
              Ergebnis ist bewusst ehrlich: Manchmal reicht eine einfache
              QR-Speisekarte aus. Viele Restaurants brauchen aber eine Lösung,
              die mit Menüverwaltung, Reservierung, Website und zukünftigen
              Funktionen mitwachsen kann.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>

        <section id="definition" className="acquaintance">
          <h2>Was bedeutet ein Vergleich digitaler Speisekarten?</h2>

          <p>
            Digitale Speisekarten zu vergleichen bedeutet nicht nur zu fragen:
            „Welcher Anbieter ist am günstigsten?“ Die bessere Frage lautet:
            Welche Lösung passt zu Ihrem Restaurant, Ihrem Team, Ihren Gästen
            und Ihren zukünftigen Plänen?
          </p>

          <p>
            Eine digitale Speisekarte kann vieles bedeuten: eine PDF-Datei mit
            QR-Code, eine einfache mobile Menüseite oder eine größere
            Restaurant-Plattform mit Speisekarte, QR-Code, Reservierung,
            mehrsprachigen Inhalten und späteren Bestellmöglichkeiten.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Mobile Ansicht.</li>
            <li className="text__seo-li">Zugang per QR-Code.</li>
            <li className="text__seo-li">Einfache Menüverwaltung.</li>
            <li className="text__seo-li">Fotos und Beschreibungen.</li>
            <li className="text__seo-li">Preise und Verfügbarkeit.</li>
            <li className="text__seo-li">Mehrsprachige Unterstützung.</li>
            <li className="text__seo-li">Reservierung.</li>
            <li className="text__seo-li">Website oder Restaurantseite.</li>
            <li className="text__seo-li">Zukünftige Erweiterungen.</li>
          </ul>

          <h3>Vergleichen Sie den Arbeitsablauf, nicht nur den Preis</h3>

          <p>
            Ein günstiges Tool kann schnell frustrierend werden, wenn die
            Speisekarte schwer zu aktualisieren ist, der QR-Code-Ablauf unklar
            bleibt oder Gäste Probleme auf dem Smartphone haben. Ein sinnvoller
            Vergleich sollte deshalb immer die echte Arbeit im Restaurant
            berücksichtigen.
          </p>
        </section>

        <section id="criteria" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Die wichtigsten Vergleichskriterien</h2>

            <p>
              Eine gute digitale Speisekarte muss nicht jede mögliche Funktion
              haben. Sie sollte aber die wichtigsten praktischen Fragen klar
              beantworten.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Grundlegende Kriterien</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>Mobile Nutzung:</strong> Öffnet sich die
                    Speisekarte schnell und übersichtlich auf dem Smartphone?
                  </li>
                  <li className="text__seo-li">
                    <strong>Menüverwaltung:</strong> Lassen sich Preise, Fotos
                    und Verfügbarkeit einfach ändern?
                  </li>
                  <li className="text__seo-li">
                    <strong>QR-Code:</strong> Ist der QR-Code einfach zu nutzen,
                    zu testen und zu drucken?
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Kriterien für Wachstum</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    <strong>Design:</strong> Wirkt die Speisekarte professionell
                    und passend zum Restaurant?
                  </li>
                  <li className="text__seo-li">
                    <strong>Skalierbarkeit:</strong> Können Reservierung,
                    Website oder Bestellung später ergänzt werden?
                  </li>
                  <li className="text__seo-li">
                    <strong>Preismodell:</strong> Ist klar, was kostenlos ist
                    und ab wann ein Upgrade notwendig wird?
                  </li>
                </ul>
              </div>
            </div>

            <p>
              Der beste Anbieter ist nicht immer der mit der längsten
              Funktionsliste. Der beste Anbieter ist der, den Ihr Restaurant im
              Alltag wirklich nutzt.
            </p>
          </div>
        </section>

        <section id="provider-types" className="acquaintance">
          <h2>Welche Arten von Anbietern für digitale Speisekarten gibt es?</h2>

          <p>
            Digitale Speisekarten gibt es in verschiedenen Formen. Um fair zu
            vergleichen, sollten Sie zuerst verstehen, welche Art von Lösung Sie
            gerade betrachten.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>PDF mit QR-Code</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Schneller Start.</li>
                <li className="text__seo-li">Oft günstig oder kostenlos.</li>
                <li className="text__seo-li">
                  Weniger komfortabel auf Smartphones.
                </li>
                <li className="text__seo-li">
                  Schwierig, wenn Änderungen häufig vorkommen.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col">
              <h3>Einfache Menüseite</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Bessere mobile Erfahrung.</li>
                <li className="text__seo-li">
                  Kategorien und Produkte sind möglich.
                </li>
                <li className="text__seo-li">
                  Gut für kleine Restaurants oder Cafés.
                </li>
                <li className="text__seo-li">
                  Erweiterungen sind oft begrenzt.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Restaurant-Plattform</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Speisekarte, QR-Code und Restaurantseite sind verbunden.
                </li>
                <li className="text__seo-li">
                  Reservierung oder Bestellung können später ergänzt werden.
                </li>
                <li className="text__seo-li">
                  Besser für langfristige Entwicklung.
                </li>
                <li className="text__seo-li">
                  Muss trotzdem einfach zu bedienen bleiben.
                </li>
              </ul>
            </div>
          </div>

          <VisualIdea>
            Drei Vergleichskarten auf dem Schreibtisch eines Restaurantinhabers:
            PDF mit QR-Code, einfache mobile Menüseite und vollständige
            Restaurant-Plattform.
          </VisualIdea>
        </section>

        <section id="pricing" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              Kosten vergleichen: kostenlos, günstig oder langfristig sinnvoll?
            </h2>

            <p>
              Viele Restaurants suchen zuerst nach einer kostenlosen digitalen
              Speisekarte. Das ist verständlich. Ein kostenloser Plan kann ein
              guter Start sein, wenn Sie testen möchten, ob QR-Speisekarte und
              Online-Verwaltung im echten Tagesgeschäft funktionieren.
            </p>

            <p>
              Trotzdem ist der Einstiegspreis nicht das Einzige, was zählt.
              Wichtig ist auch, was passiert, wenn das Restaurant wächst oder
              zusätzliche Funktionen benötigt.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Was ist kostenlos?</li>
              <li className="text__seo-li">Was kostet später extra?</li>
              <li className="text__seo-li">Ist der QR-Code enthalten?</li>
              <li className="text__seo-li">Ist Menüverwaltung enthalten?</li>
              <li className="text__seo-li">Ist Reservierung möglich?</li>
              <li className="text__seo-li">Gibt es eine Restaurantseite?</li>
              <li className="text__seo-li">
                Sind mehrsprachige Inhalte möglich?
              </li>
              <li className="text__seo-li">
                Kann das System später mitwachsen?
              </li>
            </ul>

            <div className="seoCompareSmall">
              <div>
                <strong>Verwandte Seite</strong>
                <p>
                  <Link to="/de/kostenlose-digitale-speisekarte">
                    Kostenlose digitale Speisekarte
                  </Link>
                </p>
              </div>

              <div>
                <strong>Verwandte Seite</strong>
                <p>
                  <Link to="/de/speisekarte-online-verwalten">
                    Speisekarte online verwalten
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="features" className="acquaintance">
          <h2>Welche Funktionen sind wirklich wichtig?</h2>

          <p>
            Nicht jede Funktion ist für jedes Restaurant wichtig. Ein kleines
            Café braucht am Anfang vielleicht nur eine QR-Speisekarte. Ein
            Restaurant mit Reservierungen, Terrasse und wechselnden Angeboten
            benötigt oft mehr Struktur.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">Speisekarte online anzeigen.</li>
            <li className="text__seo-li">QR-Code verwenden.</li>
            <li className="text__seo-li">Preise bearbeiten.</li>
            <li className="text__seo-li">Gerichte ausblenden.</li>
            <li className="text__seo-li">Reservierung hinzufügen.</li>
            <li className="text__seo-li">Später erweitern.</li>
          </ol>

          <p>
            Die Reihenfolge ist wichtig: Zuerst muss die Grundlage zuverlässig
            funktionieren. Danach können zusätzliche Funktionen gezielt ergänzt
            werden.
          </p>

          <div className="seoCompareSmall">
            <div>
              <strong>Verwandte Seite</strong>
              <p>
                <Link to="/de/digitale-speisekarte">
                  Digitale Speisekarte
                </Link>
              </p>
            </div>

            <div>
              <strong>Verwandte Seite</strong>
              <p>
                <Link to="/de/online-tischreservierung">
                  Online-Tischreservierung
                </Link>
              </p>
            </div>
          </div>
        </section>

        <section id="seo" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>
              Warum Sichtbarkeit und eine Restaurantseite beim Vergleich wichtig
              sind
            </h2>

            <p>
              Eine digitale Speisekarte ist nicht nur für Gäste am Tisch
              wichtig. Sie kann auch vor dem Besuch helfen: wenn Gäste online
              suchen, Preise prüfen, Fotos ansehen oder entscheiden, ob sie
              reservieren möchten.
            </p>

            <p>
              Deshalb sollte ein Vergleich noch eine weitere Frage enthalten:
              Bietet der Anbieter nur einen Menü-Link an, oder kann die Lösung
              Teil einer besseren digitalen Präsenz für das Restaurant werden?
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Nur Menü-Link</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Vor allem nützlich, nachdem der Gast den QR-Code bereits
                    hat.
                  </li>
                  <li className="text__seo-li">
                    Begrenzte Unterstützung für Online-Sichtbarkeit.
                  </li>
                  <li className="text__seo-li">
                    Restaurantinformationen bleiben oft verstreut.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Speisekarte + Restaurantseite</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Die Speisekarte kann die Online-Auffindbarkeit unterstützen.
                  </li>
                  <li className="text__seo-li">
                    Restaurantinformationen werden klarer dargestellt.
                  </li>
                  <li className="text__seo-li">
                    Reservierung und Kontakt können verbunden werden.
                  </li>
                </ul>
              </div>
            </div>

            <div className="seoCompareSmall">
              <div>
                <strong>Verwandte Seite</strong>
                <p>
                  <Link to="/de/restaurant-website">
                    Restaurant-Website
                  </Link>
                </p>
              </div>

              <div>
                <strong>Verwandte Seite</strong>
                <p>
                  <Link to="/de/restaurant-digitalisierung">
                    Restaurant-Digitalisierung
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="mistakes" className="acquaintance">
          <h2>Häufige Fehler beim Vergleich digitaler Speisekarten</h2>

          <p>
            Ein Vergleich ist nur dann hilfreich, wenn er praktisch ist. Ein
            Anbieter kann in einer Preistabelle gut aussehen und trotzdem nicht
            zu Ihrem echten Restaurantalltag passen.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Häufige Fehler</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Nur auf den niedrigsten Preis achten.
                </li>
                <li className="text__seo-li">
                  Eine PDF-Datei mit einer echten digitalen Speisekarte
                  verwechseln.
                </li>
                <li className="text__seo-li">
                  Die mobile Ansicht nicht testen.
                </li>
                <li className="text__seo-li">
                  Menüverwaltung unterschätzen.
                </li>
                <li className="text__seo-li">
                  Keine Fragen zur Zukunft stellen.
                </li>
                <li className="text__seo-li">
                  Bewertungen vertrauen, ohne das Tool selbst zu testen.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Besser</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Den Ablauf mit Ihrer eigenen Speisekarte testen.
                </li>
                <li className="text__seo-li">
                  Die Smartphone-Erfahrung prüfen.
                </li>
                <li className="text__seo-li">
                  Einen Preis ändern und ein Gericht ausblenden.
                </li>
                <li className="text__seo-li">
                  QR-Code und Restaurantseite prüfen.
                </li>
                <li className="text__seo-li">
                  Fragen, ob Reservierung oder Bestellung später ergänzt werden
                  können.
                </li>
                <li className="text__seo-li">
                  Den Nutzen vergleichen, nicht nur Funktionslisten.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="de" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>Häufige Fragen zum Vergleich digitaler Speisekarten</h2>

          <div className="seoFaq">
            <details>
              <summary>Was ist die beste digitale Speisekarte?</summary>
              <p>
                Die beste Lösung hängt vom Restaurant ab. Ein kleines Café
                braucht andere Funktionen als ein Restaurant mit Reservierungen,
                Terrasse, mehreren Sprachen oder wechselnden Angeboten.
              </p>
            </details>

            <details>
              <summary>Reicht ein kostenloser Anbieter aus?</summary>
              <p>
                Ein kostenloser Anbieter kann für den Anfang ausreichen. Die
                wichtige Frage ist, ob Sie Menüverwaltung, QR-Code-Zugang und
                mobile Ansicht realistisch testen können.
              </p>
            </details>

            <details>
              <summary>Reicht eine PDF mit QR-Code aus?</summary>
              <p>
                Für einen sehr einfachen Start kann das ausreichen. Wenn Sie
                Preise, Fotos, Tagesangebote oder Verfügbarkeit aktualisieren
                möchten, ist eine bearbeitbare Online-Speisekarte meistens
                besser.
              </p>
            </details>

            <details>
              <summary>Was sollte ich zuerst prüfen?</summary>
              <p>
                Besonders wichtig sind mobile Ansicht, einfache Bearbeitung,
                QR-Code, aktuelle Preise, Fotos, Verfügbarkeit und spätere
                Erweiterbarkeit.
              </p>
            </details>

            <details>
              <summary>Brauche ich sofort eine große Plattform?</summary>
              <p>
                Nein. Klein zu starten ist oft besser. Die Lösung sollte aber
                wachsen können, wenn Reservierung, mehrsprachige Inhalte oder
                Bestellung später wichtig werden.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="de"
          group="business"
          exclude="/de/digitale-speisekarte-vergleich"
        />
      </article>
    </main>
  );
}